import * as React from 'react';
import * as style from './MultiSelectList.styl';

// This was designed to function similarly to our ReactSelect component;
// specifically, as with 'multiselect' mode in ReactSelect,
// the MultiSelectList component's onChange prop always passes the list of selected items.

interface IMultiSelectItemProps<T> {
  getItemId?: (item: T) => string; // defaults to 'id' prop
  getItemComponent: (item: T) => JSX.Element;
  item: T;
  onCheck: (item: T) => void;
  selected: boolean;
}

interface IMultiSelectListProps<T> {
  headerText?: string;
  items: T[];
  defaultToChecked?: boolean;
  getItemId?: (item: T) => string; // defaults to 'id' prop
  getItemSelected?: (item: T) => boolean;
  getItemComponent: (item: T) => JSX.Element;
  onChange: (items: T[]) => void;
}

interface IMultiSelectListState {
  initialized: boolean;
  selectedStateByIdentifier: Record<string, boolean>;
  getItemId: (account: any) => string;
}

function MultiSelectListItem<T>(props: IMultiSelectItemProps<T>) {
  const { getItemId, item, getItemComponent, onCheck, selected } = props;

  const identifier = getItemId(item);
  const itemNode = getItemComponent(item);
  return (
    <div className="multi-select-list-item" key={`multi-select-list-item-${identifier}`}>
      <div className="checkBoxContainer">
        <input id={`checkbox-${identifier}`} type="checkbox" className="checkbox" onChange={() => onCheck(item)} checked={selected} />
        <label htmlFor={`checkbox-${identifier}`} />
      </div>
      <div className="multi-select-list-item-contents">{itemNode}</div>
    </div>
  );
}

export class MultiSelectList<T> extends React.Component<IMultiSelectListProps<T>, IMultiSelectListState> {
  static getDerivedStateFromProps(nextProps: IMultiSelectListProps<any>, lastState: IMultiSelectListState) {
    if (lastState.initialized) {
      return null;
    }
    const { items, defaultToChecked, getItemSelected } = nextProps;
    let { getItemId } = nextProps;
    if (!getItemId) {
      getItemId = (item: any) => item.id;
    }
    const selectedStateByIdentifier: Record<string, boolean> = {};

    for (const item of items) {
      const identifier = getItemId(item);
      if (!identifier) {
        throw new Error("Items must have an 'id' prop or a getItemId getter must be provided");
      }
      if (getItemSelected) {
        selectedStateByIdentifier[identifier] = getItemSelected(item);
      } else if (defaultToChecked) {
        selectedStateByIdentifier[identifier] = true;
      } else {
        selectedStateByIdentifier[identifier] = false;
      }
    }

    const initialized = true;
    return {
      getItemId,
      initialized,
      selectedStateByIdentifier
    };
  }

  state: IMultiSelectListState = { initialized: false, selectedStateByIdentifier: {}, getItemId: (item: any) => item.id };

  onCheck(checkedItem: T) {
    const selectedStateByIdentifier = { ...this.state.selectedStateByIdentifier };
    const identifier = this.state.getItemId(checkedItem);
    selectedStateByIdentifier[identifier] = !selectedStateByIdentifier[identifier];
    const { items, onChange } = this.props;
    const { getItemId } = this.state;
    const selectedItems = items.filter((item) => selectedStateByIdentifier[getItemId(item)]);
    this.setState({ selectedStateByIdentifier });
    return onChange(selectedItems);
  }

  onCheckAll(allSelected: boolean) {
    const selectedStateByIdentifier = { ...this.state.selectedStateByIdentifier };
    for (const key of Object.keys(selectedStateByIdentifier)) {
      selectedStateByIdentifier[key] = allSelected;
    }
    this.setState({ selectedStateByIdentifier });
    if (allSelected) {
      return this.props.onChange(Array.from(this.props.items));
    }
    return this.props.onChange([]);
  }

  render() {
    const { getItemComponent, headerText, items } = this.props;
    const { getItemId, selectedStateByIdentifier } = this.state;

    const onCheck = (item: T) => this.onCheck(item);

    const itemsToDisplay = items.map((item) =>
      // eslint-disable-next-line new-cap
      MultiSelectListItem({
        getItemId,
        getItemComponent,
        item,
        onCheck,
        selected: selectedStateByIdentifier[getItemId(item)]
      })
    );

    let allSelected = true;
    for (const key of Object.keys(selectedStateByIdentifier)) {
      if (!selectedStateByIdentifier[key]) {
        allSelected = false;
        break;
      }
    }

    return (
      <div className={`multi-select-list ${style.global}`}>
        <div className="multi-select-list-header">
          <div className="checkBoxContainer">
            <input
              id={'checkbox-multiselect'}
              type="checkbox"
              className="checkbox"
              onChange={() => this.onCheckAll(!allSelected)}
              checked={allSelected}
            />
            <label htmlFor={'checkbox-multiselect'} />
          </div>
          <label className="multi-select-list-header-text">{headerText || 'Select All'}</label>
        </div>
        <div className="multi-select-list-items">{itemsToDisplay}</div>
      </div>
    );
  }
}
